<template>
  <div class="mt-2 h-[250px] overflow-auto pr-1">
    <div v-if="isLoading" class="flex items-center justify-center">
      <v-progress-circular color="primary" size="172" indeterminate />
    </div>

    <v-row v-else dense>
      <v-col v-if="permissionGroups.length || permissionUsers.length || filterText" cols="12">
        <div class="mb-3 pr-2">
          <div
            v-if="!showFilterInput"
            class="flex items-center justify-between w-full border border-solid border-gray-400 rounded bg-white px-3 py-2 cursor-pointer hover:border-primary hover:bg-gray-50 transition-colors"
            title="Clique para buscar e editar permissões"
            @click="emit('update:showFilterInput', true)"
          >
            <div class="flex flex-col">
              <strong class="text-sm text-gray-800">Gerenciar Permissões de Acesso</strong>
              <span class="text-[11px] text-gray-500">Clique aqui para filtrar e editar</span>
            </div>
            <v-icon :icon="mdiMagnify" color="primary" />
          </div>

          <v-text-field
            v-else
            :model-value="filterText"
            @update:model-value="emit('update:filterText', $event)"
            density="compact"
            variant="outlined"
            color="primary"
            label="Digite o nome do usuário ou grupo"
            placeholder="Pressione Esc para cancelar"
            hide-details
            autofocus
            class="pt-0 mt-0"
            :loading="isLoadingFilter"
          >
            <template v-slot:append-inner>
              <v-icon class="cursor-pointer" :icon="mdiClose" @click="emit('clear-filter')" />
            </template>
          </v-text-field>
        </div>

        <PermissionsAccordionItem
          v-for="subject in permissionGroups"
          :key="subject.subject_id"
          :subject="subject"
          :is-expanded="showInheritance[subject.subject_id]"
          :is-marked-for-removal="isMarkedForRemoval(subject.subject_id)"
          :can-edit-access-level="canEditAccessLevel"
          :selectable-permissions="getSelectablePermissions(subject)"
          :sorted-permissions="getSortedPermissionsForDisplay(subject)"
          :get-relation-label="getRelationLabel"
          :resolve-resource-name="resolveResourceName"
          :pending-relation="getPendingRelation(subject.subject_id)"
          @toggle="emit('toggle-inheritance', subject.subject_id)"
          @change-relation="(val) => emit('track-change', { subject, val })"
          @toggle-removal="emit('toggle-removal', subject)"
        />

        <PermissionsAccordionItem
          v-for="subject in permissionUsers"
          :key="subject.subject_id"
          :subject="subject"
          :is-expanded="showInheritance[subject.subject_id]"
          :is-marked-for-removal="isMarkedForRemoval(subject.subject_id)"
          :can-edit-access-level="canEditAccessLevel"
          :selectable-permissions="getSelectablePermissions(subject)"
          :sorted-permissions="getSortedPermissionsForDisplay(subject)"
          :get-relation-label="getRelationLabel"
          :resolve-resource-name="resolveResourceName"
          :pending-relation="getPendingRelation(subject.subject_id)"
          @toggle="emit('toggle-inheritance', subject.subject_id)"
          @change-relation="(val) => emit('track-change', { subject, val })"
          @toggle-removal="emit('toggle-removal', subject)"
        />

        <div
          v-if="!permissionGroups.length && !permissionUsers.length && filterText"
          class="py-4 text-center text-gray-500 text-sm"
        >
          Nenhum resultado encontrado para "{{ filterText }}"
        </div>

        <div
          v-if="hasMore"
          v-intersect="onEndReached"
          class="py-4 text-center flex justify-center w-full"
        >
          <v-progress-circular
            v-if="loadingMore"
            indeterminate
            size="24"
            width="2"
            color="primary"
          />
        </div>
      </v-col>

      <v-col v-else cols="12">
        <div class="flex items-center justify-between mb-2 pr-2 h-[40px]">
          <strong class="title">Grupos ou Pessoas com acesso</strong>
        </div>
        <div class="flex mt-4">
          <CardNoSharedResource />
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import { mdiClose, mdiMagnify } from '@mdi/js'
import type { AuthorizationRelationResponseExpanded } from '@/Network/Types/Responses/AuthorizationResponses'
import CardNoSharedResource from '../../Cards/CardNoSharedResource.vue'
import PermissionsAccordionItem, {
  type SelectablePermission,
  type SortedPermItem
} from './PermissionsAccordionItem.vue'

defineProps<{
  permissionGroups: AuthorizationRelationResponseExpanded[]
  permissionUsers: AuthorizationRelationResponseExpanded[]
  filterText: string
  showFilterInput: boolean
  isLoading: boolean
  isLoadingFilter: boolean
  hasMore: boolean
  loadingMore: boolean
  showInheritance: Record<number, boolean>
  canEditAccessLevel: boolean
  isMarkedForRemoval: (id: number) => boolean
  getRelationLabel: (relation: string) => string
  getSelectablePermissions: (
    subject: AuthorizationRelationResponseExpanded
  ) => SelectablePermission[]
  getSortedPermissionsForDisplay: (
    subject: AuthorizationRelationResponseExpanded
  ) => SortedPermItem[]
  resolveResourceName: (permItem: SortedPermItem) => string
  getPendingRelation: (subjectId: number) => string | null
}>()

const emit = defineEmits<{
  'update:filterText': [value: string]
  'update:showFilterInput': [value: boolean]
  'clear-filter': []
  'toggle-inheritance': [id: number]
  'track-change': [payload: { subject: AuthorizationRelationResponseExpanded; val: string }]
  'toggle-removal': [subject: AuthorizationRelationResponseExpanded]
  'end-reached': []
}>()

const onEndReached = (isIntersecting: boolean) => {
  if (isIntersecting) emit('end-reached')
}
</script>
